<script setup lang="ts">
import type { EventVault } from "@/models"
import { useTemplateRef } from "vue"
import { Menu, MenuItem } from "@/components/legacy"
import { Button } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventVaultStatus } from "@/models"

interface ActionsMenuProps {
  eventVault: EventVault
}

const { eventVault } = defineProps<ActionsMenuProps>()

const emit = defineEmits<{
  (e: "showClose", eventVaultId?: number): void
  (e: "showExport", eventVaultId?: number): void
  (e: "showStart", eventVaultId?: number): void
}>()

const { t } = useTranslation()

const menuRef = useTemplateRef("menuRef")

function onItemClick(cb: (v?: number) => void, dismiss: boolean = false) {
  if (dismiss) {
    menuRef.value?.toggle()
  }

  cb()
}
</script>

<template>
  <Menu ref="menuRef">
    <template #activator="{ toggle }">
      <Button
        prefix-icon="MenuEllipsisHorizontal"
        variant="flat"
        class="flex-col gap-0"
        @click="toggle"
      >
        <span class="sr-only">t('host_event_card.open_actions_menu')</span>
        <span class="text-sm">{{ t("more") }}</span>
      </Button>
    </template>
    <MenuItem
      :item="{
        key: 'edit',
        label: t('host_event_card.edit'),
        to: `/event/edit/${eventVault.id}`,
      }"
    />
    <MenuItem
      :item="{
        key: 'upgrade',
        label: t('host_event_card.upgrade'),
        ...(eventVault.freemiumMode && eventVault.upgradeUrl
          ? { href: eventVault.upgradeUrl }
          : { to: `/event/upgrade/${eventVault.id}` }),
      }"
    />
    <MenuItem
      :item="{
        key: 'export',
        label: t('host_dashboard.export'),
        onClick: () =>
          onItemClick(() => emit('showExport', eventVault.id), true),
      }"
    />
    <MenuItem
      v-if="
        eventVault.status === EventVaultStatus.Draft
          || eventVault.status === EventVaultStatus.Ready
      "
      :item="{
        key: 'start',
        label: t('host_event_card.start'),
        onClick: () =>
          onItemClick(() => emit('showStart', eventVault.id), true),
      }"
    />
    <MenuItem
      v-else-if="eventVault.status === EventVaultStatus.Live"
      :item="{
        key: 'close',
        label: t('host_event_card.close'),
        onClick: () =>
          onItemClick(() => emit('showClose', eventVault.id), true),
      }"
    />
  </Menu>
</template>
